import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material'
import {
  Close as CloseIcon
} from '@mui/icons-material'
import { ActionButton } from '@emerald-works-nova/components'
import { useStyles } from './create-page-modal-style'

const CreatePageModal = ({ open, onClose, onCreatePage }) => {
  const classes = useStyles()
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    slug: '',
    pageType: 'paid-consumer-homepage',
    status: 'draft'
  })
  const [errors, setErrors] = useState({})

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Auto-generate slug from name
    if (field === 'name') {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
      setFormData(prev => ({
        ...prev,
        slug: slug ? `/${slug}` : ''
      }))
    }
    // Clear errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Page name is required'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Page description is required'
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Page URL is required'
    } else if (!/^\/[a-z0-9-]*$/.test(formData.slug)) {
      newErrors.slug = 'Invalid URL format. Use lowercase letters, numbers, and hyphens only.'
    }

    if (!formData.pageType) {
      newErrors.pageType = 'Page type is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (validateForm()) {
      const pageData = {
        id: `page-${Date.now()}`,
        name: formData.name.trim(),
        description: formData.description.trim(),
        slug: formData.slug.trim(),
        pageType: formData.pageType,
        status: formData.status,
        isDefault: false,
        canDelete: true,
        lastModified: new Date().toISOString(),
        sections: []
      }

      onCreatePage(pageData)
      handleClose()
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      slug: '',
      pageType: 'paid-consumer-homepage',
      status: 'draft'
    })
    setErrors({})
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      className={classes.dialog}
      data-test='create-page-modal'
    >
      <DialogTitle className={classes.dialogTitle}>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <Typography variant='h6' className={classes.title}>
            Create New Page
          </Typography>
          <IconButton onClick={handleClose} size='small'>
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant='body2' color='textSecondary' className={classes.subtitle}>
          Create a new page configuration for the Toolkit. You can configure sections and content after creation.
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box className={classes.form}>
          <TextField
            fullWidth
            label='Page Name'
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            placeholder='e.g., About Us, Contact, Services'
            data-test='page-name-input'
            className={classes.formField}
          />

          <TextField
            fullWidth
            label='Description'
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            placeholder='Brief description of what this page contains'
            multiline
            rows={3}
            data-test='page-description-input'
            className={classes.formField}
          />

          <TextField
            fullWidth
            label='Page URL'
            value={formData.slug}
            onChange={(e) => handleInputChange('slug', e.target.value)}
            error={!!errors.slug}
            helperText={errors.slug || 'URL will be automatically generated from the page name'}
            placeholder='/about-us'
            data-test='page-slug-input'
            className={classes.formField}
          />

          <FormControl
            fullWidth
            error={!!errors.pageType}
            className={classes.formField}
          >
            <InputLabel id='page-type-label'>Page Type</InputLabel>
            <Select
              labelId='page-type-label'
              label='Page Type'
              value={formData.pageType}
              onChange={(e) => handleInputChange('pageType', e.target.value)}
              data-test='page-type-select'
            >
              <MenuItem value='paid-consumer-homepage'>Paid Consumer Homepage</MenuItem>
            </Select>
            <FormHelperText>
              {errors.pageType || 'Select the type of page you want to create'}
            </FormHelperText>
          </FormControl>

        </Box>
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <ActionButton
          variant='secondary'
          onClick={handleClose}
          dataTest='cancel-create-page'
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant='primary'
          onClick={handleSubmit}
          dataTest='confirm-create-page'
        >
          Create Page
        </ActionButton>
      </DialogActions>
    </Dialog>
  )
}

export default CreatePageModal
